import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, X } from 'lucide-react';

const SUPPORTED_LANGS = {
  es: { native: 'Español', flag: '🇪🇸' },
  fr: { native: 'Français', flag: '🇫🇷' },
  pt: { native: 'Português', flag: '🇧🇷' },
};

const STORAGE_KEY = 'eili_lang_prompted';

export default function LanguageDetectBanner() {
  const {
    t
  } = useTranslation();

  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [detectedLang, setDetectedLang] = useState(null);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Already prompted before — skip
    if (localStorage.getItem(STORAGE_KEY)) return;

    // If user already chose a language explicitly (i18nextLng is set AND matches what navigator reports), skip
    const stored = localStorage.getItem('i18nextLng');

    // Get the browser's primary language (e.g., 'es-ES' → 'es')
    const browserLang = (navigator.language || navigator.userLanguage || 'en').split('-')[0].toLowerCase();

    // Only prompt if browser language is a supported non-English language
    if (!SUPPORTED_LANGS[browserLang]) {
      localStorage.setItem(STORAGE_KEY, '1');
      return;
    }

    // If stored language already matches browser language, no need to prompt
    if (stored === browserLang) {
      localStorage.setItem(STORAGE_KEY, '1');
      return;
    }

    setDetectedLang(browserLang);

    // Small delay so the page loads first
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      localStorage.setItem(STORAGE_KEY, '1');
    }, 300);
  };

  const switchLang = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem(STORAGE_KEY, '1');
    // Mark as an explicit user choice so ProtectedRoute won't
    // subsequently clobber it with the server-side default.
    try { localStorage.setItem('eili-lang-explicit', '1'); } catch { /* private mode */ }
    dismiss();
  };

  if (!visible || !detectedLang) return null;

  const langInfo = SUPPORTED_LANGS[detectedLang];

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-sm transition-all duration-300 ${
        exiting ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      }`}
      style={{ animation: exiting ? 'none' : 'slideUpBanner 0.4s ease-out' }}
      data-testid="language-detect-banner"
    >
      <div
        className="rounded-2xl p-4 shadow-2xl border backdrop-blur-xl"
        style={{
          background: 'var(--glass-bg, rgba(30,30,40,0.92))',
          borderColor: 'var(--glass-border, rgba(255,255,255,0.08))',
        }}
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 p-1 rounded-full transition-colors hover:bg-white/10"
          style={{ color: 'var(--theme-textSecondary)' }}
          data-testid="language-banner-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div
            className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'var(--theme-accent)', opacity: 0.9 }}
          >
            <Globe className="h-4.5 w-4.5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight" style={{ color: 'var(--theme-text)' }}>
              {langInfo.flag} {langInfo.native}?
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--theme-textSecondary)' }}>
              {detectedLang === 'es' && 'Detectamos tu idioma. ¿Usar Eili en Español?'}
              {detectedLang === 'fr' && 'Nous avons détecté votre langue. Utiliser Eili en Français ?'}
              {detectedLang === 'pt' && 'Detectamos seu idioma. Usar Eili em Português?'}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => switchLang(detectedLang)}
            className="flex-1 text-xs font-semibold py-2 rounded-xl transition-all active:scale-[0.97]"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            data-testid="language-banner-accept"
          >
            {detectedLang === 'es' && 'Sí, usar Español'}
            {detectedLang === 'fr' && 'Oui, en Français'}
            {detectedLang === 'pt' && 'Sim, em Português'}
          </button>
          <button
            onClick={() => switchLang('en')}
            className="px-4 text-xs font-medium py-2 rounded-xl transition-all active:scale-[0.97]"
            style={{ background: 'var(--glass-bg-active, rgba(255,255,255,0.06))', color: 'var(--theme-textSecondary)' }}
            data-testid="language-banner-english"
          >
            {t('generated.languagedetectbanner_english_1591')}
          </button>
        </div>
      </div>
      <style>{`
        @keyframes slideUpBanner {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
}
